/**
 * /field/ew — an Earthworks hand's own jobs (GET /earthworks/field/my-jobs,
 * fieldiq-api#1284, EW-4).
 *
 * THE SELECTION IS THE API'S: the jobs whose crew the caller is on that are
 * scheduled or in progress. This screen only groups them, in the API's order,
 * and says whether today's log is written and how many warnings are showing.
 * SNAKE_CASE (job_number, today_log_id, warning_count).
 *
 * The frame is Pressure Testing's field frame (PtFieldPage), imported, so the
 * verticals' field screens stay one shape.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, MapPin } from 'lucide-react';
import { useEwFieldJobs } from '../../../hooks/useEwField';
import { ewDay, ewJobStatusSkin } from '../../../lib/earthworks';
import type { EwFieldJob } from '../../../types/earthworks';
import { PtFieldBadge, PtFieldCard, PtFieldEmpty, PtFieldPage, PtFieldQueryState, PtFieldSectionTitle } from '../../pressure-testing/field/PtFieldBits';

function JobCard({ job }: { job: EwFieldJob }) {
  const warnings = job.warning_count ?? 0;
  return (
    <Link to={`/field/ew/jobs/${job.id}`} className="block" data-testid="ew-my-job">
      <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
            <PtFieldBadge skin={ewJobStatusSkin(job.status)} />
            {warnings > 0 && (
              <span
                data-testid="ew-my-job-warnings"
                className="inline-flex items-center gap-1 rounded-fi-pill border border-black/20 bg-fi-signal-amber px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink"
              >
                <AlertTriangle className="size-3" aria-hidden="true" />
                {warnings} warning{warnings === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-base font-bold text-white">{job.customer_name || '—'}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
            <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
            {job.site_name || '—'}
            {job.job_type_label ? ` · ${job.job_type_label}` : ''}
          </p>
          <p className="mt-1 flex items-center gap-1 text-xs text-fieldiq-muted" data-testid="ew-my-job-today">
            {job.today_log_id ? (
              <>
                <CheckCircle2 className="size-3.5 text-fi-green" aria-hidden="true" />
                Today&apos;s log written
              </>
            ) : job.status === 'scheduled' ? (
              `Starts ${ewDay(job.scheduled_start)}`
            ) : (
              'No log yet today'
            )}
          </p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

export default function EwMyJobsScreen() {
  const query = useEwFieldJobs();
  const jobs = query.data?.data ?? [];
  const groups = [
    { key: 'in_progress', title: 'In progress', jobs: jobs.filter((j) => j.status === 'in_progress') },
    { key: 'scheduled', title: 'Scheduled', jobs: jobs.filter((j) => j.status === 'scheduled') },
  ].filter((g) => g.jobs.length > 0);

  return (
    <PtFieldPage title="My EW Jobs" backTo="/field" testId="ew-my-jobs">
      <PtFieldQueryState query={query} subject="your earthworks jobs">
        {groups.length === 0 ? (
          <PtFieldEmpty>You are not on the crew of any open earthworks job. The office puts you on a job&apos;s crew.</PtFieldEmpty>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-labelledby={`ew-group-${g.key}`}>
              <PtFieldSectionTitle>
                <span id={`ew-group-${g.key}`}>
                  {g.title} · {g.jobs.length}
                </span>
              </PtFieldSectionTitle>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </section>
          ))
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
